import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  FormLabel,
  Textarea,
  Flex,
  useTheme,
  SimpleGrid,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import SubmitButton from "../../buttons/SubmitButton";
import { useDispatch, useSelector } from "react-redux";
import {
  createpackage,
  updatepackage,
} from "../../../store/slice/packageSlice";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import { getAllSubjects } from "../../../store/slice/subjectSlice";
import { getAllCourses } from "../../../store/slice/courseSlice";

const defaultFormData = {
  name: "",
  description: "",
  amount: "",
  displayOrder: "",
  courses: [],
  subjects: [],
  examportal: false,
  copportal: false,
};

const AddPackageModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();
  const { primaryBlueLight, primaryBlue } = theme.colors.pallete;
  const { subjects } = useSelector((state) => state.subject);
  const { courses } = useSelector((state) => state.courses);

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  useEffect(() => {
    dispatch(getAllSubjects());
    dispatch(getAllCourses());
  }, [dispatch]);

  useEffect(() => {
    if (editData) {
      setFormData({
        ...defaultFormData,
        ...editData,
      });
    } else {
      setFormData(defaultFormData);
    }
    setErrors({});
  }, [editData, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const toggleCheckboxArray = (key, id) => {
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].includes(id)
        ? prev[key].filter((item) => item !== id)
        : [...prev[key], id],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Please enter package name";
    if (!formData.description)
      newErrors.description = "Please enter description";
    if (
      !formData.amount ||
      isNaN(formData.amount) ||
      Number(formData.amount) <= 0
    )
      newErrors.amount = "Please enter a valid amount";
    if (!formData.displayOrder || isNaN(formData.displayOrder))
      newErrors.displayOrder = "Display order should be a number";
    if (!formData.validity || isNaN(formData.validity))
      newErrors.validity = "Validity should be a number";
    if (formData.subjects.length === 0)
      newErrors.subjects = "Please select at least one subject";
    if (formData.courses.length === 0)
      newErrors.courses = "Please select at least one course";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      ...formData,
      amount: Number(formData.amount),
      displayOrder: Number(formData.displayOrder),
      validity: Number(formData.validity),
    };

    setIsSubmitLoading(true);
    const action = editData
      ? updatepackage({ id: editData.id, formData: payload })
      : createpackage(payload);

    dispatch(action)
      .unwrap()
      .then(() => {
        addNotification(
          editData
            ? "Package updated successfully"
            : "Package created successfully",
          "success",
          3000
        );
        onClose();
      })
      .catch(() =>
        addNotification(
          editData ? "Error updating package" : "Error creating package",
          "error",
          3000
        )
      )
      .finally(() => setIsSubmitLoading(false));
  };

  const renderInputField = (name, label, type = "text", isRequired = true) => (
    <FormControl mt={4} isRequired={isRequired} isInvalid={errors[name]}>
      <FormLabel>{label}</FormLabel>
      <Input
        name={name}
        type={type}
        fontSize="14px"
        borderColor="gray.300"
        placeholder={`Enter ${label.toLowerCase()}`}
        value={formData[name]}
        onChange={handleChange}
      />
      {errors[name] && <Text color="red.500">{errors[name]}</Text>}
    </FormControl>
  );

  const renderCheckboxList = (label, items, keyName) => (
    <FormControl mt={4} isRequired isInvalid={errors[keyName]}>
      <FormLabel>{label}</FormLabel>
      <SimpleGrid columns={3} spacing={5}>
        {items.map((item) => (
          <Flex key={item.id} align="center">
            <Checkbox
              id={item.name || item.title}
              isChecked={formData[keyName].includes(item.id)}
              onChange={() => toggleCheckboxArray(keyName, item.id)}
              mr={2}
            />
            <Text fontSize="14px" isTruncated>
              {item.name || item.title}
            </Text>
          </Flex>
        ))}
      </SimpleGrid>
      {errors[keyName] && <Text color="red.500">{errors[keyName]}</Text>}
    </FormControl>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px" fontWeight={400} textAlign="center">
          {editData ? "Update Package" : "Add Package"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {renderInputField("name", "Package Name")}
          <FormControl mt={4} isRequired isInvalid={errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              borderColor="gray.300"
              fontSize="14px"
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange}
            />
            {errors.description && (
              <Text color="red.500">{errors.description}</Text>
            )}
          </FormControl>
          {renderInputField("amount", "Amount", "number")}
          {renderInputField("displayOrder", "Display Order")}
          {renderInputField("validity", "Validity (in days)")}

          {renderCheckboxList("Subjects", subjects, "subjects")}
          {renderCheckboxList("Courses", courses, "courses")}

          <FormControl mt={4}>
            <FormLabel>Extras</FormLabel>
            <SimpleGrid columns={2} spacing={5}>
              <Checkbox
                id="copportal"
                name="copportal"
                isChecked={formData.copportal}
                onChange={handleChange}
              >
                COP
              </Checkbox>
              <Checkbox
                id="examportal"
                name="examportal"
                isChecked={formData.examportal}
                onChange={handleChange}
              >
                Exam Portal
              </Checkbox>
            </SimpleGrid>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Flex w="full" justifyContent="center">
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              onClickHandler={handleSubmit}
              isLoading={isSubmitLoading}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPackageModal;
